import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import useForm from 'react-hook-form';
import { Table } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { connect } from 'react-redux';
import { Event, SocketContext } from '../../../../socketProvider';

import CameraHeader from './CameraHeader';
import CameraConfigRow from './CameraConfigRow';
import CameraControls from './CameraControls';
import HostInfoRow from './HostInfoRow';
import RefCheckRow from './RefCheckRow';
import CameraInfoRow from './CameraInfoRow';

import Loader from '../../../../shared/Loader';
import { displaySuccessModal, displayErrorModal } from '../../../../helpers/swal';
import { updateCameraConfigs } from '../api';
import { fetchCamera } from '../actions';

import style from './style.module.scss';

const REF_CHECK_RESULT = 'ref-check result';
const REF_CHECK_TIMEOUT_MS = 60000;
const AUTO_REF_CHECK_KEY = 'acs.autoRefCheck';
const REF_CHECK_TOAST_CONTAINER = 'camera-config-ref-check';

const formatMetric = (label, value) => (
  value != null ? `${label}: ${value.toFixed(2)}px` : null
);

const showRefCheckToast = (cameraKey, result) => {
  const isOk = result.status === 'ok';
  if (result.error) {
    toast.error(`[${cameraKey}] Ref Check failed: ${result.error}`, {
      containerId: REF_CHECK_TOAST_CONTAINER,
    });
    return;
  }

  const details = [
    result.status != null && `Status: ${result.status}`,
    formatMetric('Δx', result.delta_x),
    formatMetric('Δy', result.delta_y),
    formatMetric('Distance', result.euclidean_distance),
  ].filter(Boolean);

  toast(
    (
      <div>
        <div>
          <strong>{cameraKey}</strong>
          {' '}
          - Ref Check
          {' '}
          {isOk ? 'Ok' : 'Needs Adjustment'}
        </div>
        {details.map((detail) => <div key={detail}>{detail}</div>)}
        {result.frame_comparison_url && (
          <a href={result.frame_comparison_url} target="_blank" rel="noopener noreferrer">
            View comparison image
          </a>
        )}
      </div>
    ),
    {
      containerId: REF_CHECK_TOAST_CONTAINER,
      type: isOk ? 'success' : 'warning',
    },
  );
};

const CameraSettingsTable = ({ cameras, isFetching, fetchCameraAction }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refCheckLoadingCameras, setRefCheckLoadingCameras] = useState({});
  const [refreshLoadingCamera, setRefreshLoadingCamera] = useState(null);
  const refCheckLoadingCamerasRef = useRef({});
  const refCheckTimeoutsRef = useRef({});
  refCheckLoadingCamerasRef.current = refCheckLoadingCameras;
  const socket = useContext(SocketContext);
  const { handleSubmit, register, setValue } = useForm();

  const clearRefCheckLoading = useCallback((cameraKey) => {
    setRefCheckLoadingCameras((prev) => {
      if (!prev[cameraKey]) return prev;
      const next = { ...prev };
      delete next[cameraKey];
      return next;
    });
  }, []);

  const clearRefCheckTimeout = useCallback((cameraKey) => {
    const timeoutId = refCheckTimeoutsRef.current[cameraKey];
    if (timeoutId) {
      clearTimeout(timeoutId);
      delete refCheckTimeoutsRef.current[cameraKey];
    }
  }, []);

  const handleRefCheckResult = useCallback((cameraKey, result) => {
    if (!refCheckLoadingCamerasRef.current[cameraKey]) return;
    clearRefCheckTimeout(cameraKey);
    clearRefCheckLoading(cameraKey);
    showRefCheckToast(cameraKey, result);
    fetchCameraAction(cameraKey);
  }, [clearRefCheckLoading, clearRefCheckTimeout, fetchCameraAction]);

  const refCheckHandler = useCallback((cameraKey) => {
    if (refCheckLoadingCamerasRef.current[cameraKey]) return;
    setRefCheckLoadingCameras((prev) => ({ ...prev, [cameraKey]: true }));
    clearRefCheckTimeout(cameraKey);
    refCheckTimeoutsRef.current[cameraKey] = setTimeout(() => {
      if (!refCheckLoadingCamerasRef.current[cameraKey]) return;
      clearRefCheckLoading(cameraKey);
      clearRefCheckTimeout(cameraKey);
      toast.error(
        `[${cameraKey}] Ref Check timeout. The ACS may not be connected (ensure the camera is powered on and logic socket is connected).`,
        { containerId: REF_CHECK_TOAST_CONTAINER },
      );
    }, REF_CHECK_TIMEOUT_MS);
    socket.emit('ref-check', cameraKey);
  }, [clearRefCheckLoading, clearRefCheckTimeout, socket]);

  useEffect(() => {
    const refCheckTimeouts = refCheckTimeoutsRef.current;
    return () => {
      Object.keys(refCheckTimeouts).forEach((cameraKey) => {
        clearTimeout(refCheckTimeouts[cameraKey]);
      });
      refCheckTimeoutsRef.current = {};
    };
  }, []);

  const restartACSHandler = (cameraKey) => {
    try {
      socket.emit('restart acs', cameraKey);
      displaySuccessModal({
        text: `Restarted ACS succesfully on ${cameraKey}`,
        timer: 5000,
      });
    } catch (err) {
      displayErrorModal({
        text: `Error trying to restart ACS on ${cameraKey}, try again`,
        timer: 5000,
      });
      console.error('Error trying to restart ACS', err);
    }
  };

  const refreshCameraSettingsHandler = async (cameraKey) => {
    if (refreshLoadingCamera) return;
    setRefreshLoadingCamera(cameraKey);
    try {
      const queued = await new Promise((resolve) => {
        socket.emit('get CamSettings', cameraKey, (ack) => {
          resolve(Boolean(ack));
        });
      });

      if (!queued) {
        throw new Error('Refresh request was not queued');
      }

      displaySuccessModal({
        text: `${cameraKey} ONVIF/CGI refresh requested`,
        timer: 5000,
      });

      setTimeout(() => {
        fetchCameraAction(cameraKey);
      }, 2500);
    } catch (err) {
      displayErrorModal({
        text: `Error trying to refresh ONVIF/CGI on ${cameraKey}, try again`,
        timer: 5000,
      });
      console.error('Error trying to queue ONVIF/CGI refresh', err);
    } finally {
      setRefreshLoadingCamera(null);
    }
  };

  if (!cameras.length) {
    if (isFetching) {
      return <p>Loading available Cameras</p>;
    }
    return <p>No cameras available</p>;
  }

  const configKeys = Array.from(
    new Set(cameras.flatMap((camera) => camera.configs.map((item) => item.configKey))),
  );

  const getConfigByKey = (camera, key) => camera.configs.find((config) => config.configKey === key);

  const categorizeConfig = (configKey) => {
    const [category, subCategory, key, subKey] = configKey.split('.');
    let currentCategory = category;
    let currentLabel = subCategory;

    if (!subCategory) {
      currentCategory = 'General Settings';
      currentLabel = category;
    }

    if (category === 'acs') {
      currentCategory = key ? subCategory : 'General Settings';
      if (key) {
        currentLabel = subKey ? `${key}: ${subKey}` : key;
      } else {
        currentLabel = subCategory;
      }
    }

    return { currentCategory, currentLabel };
  };

  const onSubmit = async (data, camera = null) => {
    setIsSubmitting(true);

    let focusedCamera = camera;

    if (!camera) {
      const { activeElement } = document;
      const activeInputName = activeElement?.name;
      const cameraKeyMatch = activeInputName?.match(/cameraConfigs\.(.*?)\./);
      const focusedCameraKey = cameraKeyMatch ? cameraKeyMatch[1] : null;

      if (!focusedCameraKey) {
        console.warn('Could not determine the focused camera.');
        setIsSubmitting(false);
        return;
      }

      focusedCamera = cameras.find((cam) => cam._id === focusedCameraKey);
    }

    if (!focusedCamera) {
      console.error('Focused camera not found.');
      setIsSubmitting(false);
      return;
    }

    const cameraObj = {
      camera: focusedCamera.cameraKey,
      configs: data.cameraConfigs ? data.cameraConfigs[focusedCamera._id] : [],
      _id: focusedCamera._id,
    };

    try {
      await updateCameraConfigs(focusedCamera.cameraKey, cameraObj);
      fetchCameraAction(focusedCamera.cameraKey);
      setIsSubmitting(false);
      displaySuccessModal({
        text: `${focusedCamera.cameraKey} updated successfully`,
        timer: 5000,
      });
    } catch (err) {
      setIsSubmitting(false);
      displayErrorModal({
        text: `Error trying to update ${focusedCamera.cameraKey}, try again`,
        timer: 5000,
      });
      console.error('Error trying to update config', err);
    }
  };

  const categories = {};

  configKeys.forEach((key) => {
    if (key === AUTO_REF_CHECK_KEY) {
      return;
    }
    const { currentCategory, currentLabel } = categorizeConfig(key);
    if (!categories[currentCategory]) {
      categories[currentCategory] = [];
    }
    categories[currentCategory].push({ key, label: currentLabel });
  });

  const autoRefCheckConfig = cameras.reduce(
    (acc, camera) => acc || getConfigByKey(camera, AUTO_REF_CHECK_KEY),
    null,
  );

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit((data) => onSubmit(data))();
    }
  };

  const cgiConfigKeys = Array.from(
    new Set(
      cameras.flatMap((c) => Object.keys(c.cameraSettings?.cgiConfigs || {})),
    ),
  ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  return (
    <>
      {isSubmitting && <Loader />}
      <Event event={REF_CHECK_RESULT} handler={handleRefCheckResult} />
      <ToastContainer
        enableMultiContainer
        containerId={REF_CHECK_TOAST_CONTAINER}
        position="bottom-right"
        autoClose={5000}
      />
      <div role="button" tabIndex={0} onKeyDown={handleKeyPress} onClick={() => {}}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={style.tableContainer}>
            <Table striped hover responsive className={style.CameraSettingsTable}>
              <thead>
                <CameraHeader cameras={cameras} />
              </thead>
              <tbody>
                {Object.keys(categories).map((category) => (
                  <React.Fragment key={category}>
                    <tr>
                      <td colSpan={cameras.length + 1} className={style.ThemeYellowBackground}>
                        {category}
                      </td>
                    </tr>
                    {categories[category].map(({ key, label }) => {
                      const config = cameras.reduce(
                        (acc, camera) => acc || getConfigByKey(camera, key),
                        null,
                      );
                      if (!config) return null;
                      return (
                        <CameraConfigRow
                          key={key}
                          cameras={cameras}
                          config={config}
                          label={label}
                          configKey={key}
                          register={register}
                          setValue={setValue}
                          getConfigByKey={getConfigByKey}
                        />
                      );
                    })}
                  </React.Fragment>
                ))}
                <HostInfoRow cameras={cameras} />
                <RefCheckRow
                  cameras={cameras}
                  autoRefCheckConfig={autoRefCheckConfig}
                  register={register}
                  setValue={setValue}
                  getConfigByKey={getConfigByKey}
                  refCheckHandler={refCheckHandler}
                  refCheckLoadingCameras={refCheckLoadingCameras}
                />
                <tr>
                  <td colSpan={cameras.length + 1} className={style.ThemeYellowBackground}>
                    Onvif camera settings
                  </td>
                </tr>
                <CameraInfoRow cameras={cameras} />
                {cgiConfigKeys.length > 0 && (
                  <>
                    <tr>
                      <td colSpan={cameras.length + 1} className={style.ThemeYellowBackground}>
                        CGI Configs
                      </td>
                    </tr>
                    {cgiConfigKeys.map((cgiKey) => {
                      const values = cameras.map(
                        (c) => String(c.cameraSettings?.cgiConfigs?.[cgiKey] ?? ''),
                      );
                      const hasMismatch = new Set(values).size > 1;
                      return (
                        <tr
                          key={cgiKey}
                          className={`${style.TopLabel} ${hasMismatch ? style.rowMismatch : ''}`.trim()}
                        >
                          <td className={style.SideLabel}>{cgiKey}</td>
                          {cameras.map((camera) => {
                            const value = camera.cameraSettings?.cgiConfigs?.[cgiKey];
                            return (
                              <td key={camera._id}>
                                <div className="form-group">
                                  <div className="form-row justify-content-center">
                                    <pre className="m-0 d-flex flex-column justify-content-around text-center">
                                      {value == null ? 'No Info Available' : String(value)}
                                    </pre>
                                  </div>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </>
                )}
                <CameraControls
                  cameras={cameras}
                  restartACSHandler={restartACSHandler}
                  refreshCameraSettingsHandler={refreshCameraSettingsHandler}
                  refreshLoadingCamera={refreshLoadingCamera}
                  handleSubmit={handleSubmit}
                  onSubmit={onSubmit}
                  setValue={setValue}
                />
              </tbody>
            </Table>
          </div>
        </form>
      </div>
    </>
  );
};

export default connect(
  ({ cameraConfigs: { isFetching } }) => ({ isFetching }),
  { fetchCameraAction: fetchCamera },
)(CameraSettingsTable);
